import React, {useCallback, useMemo} from "react";
import Stepper from "../../../../../../../components/app-specific/stepper";
import {setReduxDialogState} from "../../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from "react-redux";
import {PrintBrochureViewSteps} from "../../index";

const PrintCatalogueViewStepperSection = ({
                                              productSelectionState,
                                              configureGroupsState,
                                              reviewState,
                                              step,
                                              setStep,
                                              updatingStep,
                                          }) => {
    const dispatch = useDispatch();

    const selectedCategoriesLength = useMemo(() => productSelectionState.selectedCategories.length ?? 0, [productSelectionState.selectedCategories.length]);
    const selectedProductsLength = useMemo(() => productSelectionState.selectedProducts?.length ?? 0, [productSelectionState.selectedProducts?.length]);
    const selectedTagsLength = useMemo(() => productSelectionState.selectedTags?.length ?? 0, [productSelectionState.selectedTags?.length]);
    const groupsLength = useMemo(() => configureGroupsState.groups.filter(e => !e.isRemoved).length, [configureGroupsState.groups]);
    const reviewStateProductsLength = useMemo(() => reviewState.groups.reduce((a, e) => a + e.products.filter(e => !e.isRemoved).length, 0), [reviewState.groups]);

    const steps = useMemo(() => {
        const result = [
            {id: PrintBrochureViewSteps.productSelection},
            {id: PrintBrochureViewSteps.configureGroups},
            {id: PrintBrochureViewSteps.review},
        ]
        const changedLabels = {
            [PrintBrochureViewSteps.productSelection]: [
                selectedCategoriesLength ? `${(selectedCategoriesLength)} ${selectedCategoriesLength === 1 ? 'Category' : 'Categories'}` : '',
                selectedProductsLength ? `${selectedProductsLength} ${(selectedProductsLength) === 1 ? 'Product' : 'Products'}` : '',
                selectedTagsLength ? `${selectedTagsLength} ${selectedTagsLength === 1 ? 'Tag' : 'Tags'}` : '',
            ]
                .filter(Boolean)
                .join(' and ').concat(' selected'),
            [PrintBrochureViewSteps.configureGroups]: `${groupsLength} ${groupsLength === 1 ? 'Group' : 'Groups'} selected`,
            [PrintBrochureViewSteps.review]: `${reviewStateProductsLength} ${reviewStateProductsLength === 1 ? "Product" : "Products"} selected`,
        }
        const defaultLabels = {
            [PrintBrochureViewSteps.productSelection]: 'Select Category',
            [PrintBrochureViewSteps.configureGroups]: 'Configure Groups',
            [PrintBrochureViewSteps.review]: 'Review and Finalize',
        }
        const stepPassed = {
            [PrintBrochureViewSteps.productSelection]: step === PrintBrochureViewSteps.configureGroups || step === PrintBrochureViewSteps.review,
            [PrintBrochureViewSteps.configureGroups]: step === PrintBrochureViewSteps.review,
            [PrintBrochureViewSteps.review]: false,
        }
        const stepsDisabledStatus = {
            [PrintBrochureViewSteps.productSelection]: step === PrintBrochureViewSteps.review || (!selectedTagsLength && !selectedProductsLength && !selectedCategoriesLength),
            [PrintBrochureViewSteps.configureGroups]: !groupsLength,
            [PrintBrochureViewSteps.review]: step === PrintBrochureViewSteps.productSelection || step === PrintBrochureViewSteps.configureGroups,
        }
        return result.map(e => ({
            id: e.id,
            label: stepPassed[e.id] ? changedLabels[e.id] : defaultLabels[e.id],
            disabled: stepsDisabledStatus[e.id] || updatingStep,
        }));
    }, [groupsLength, selectedCategoriesLength, selectedProductsLength, selectedTagsLength, step, updatingStep, reviewStateProductsLength])

    /**
     * Step change handler.
     *
     * If the current step is the review step, a confirmation dialog will be shown to the user.
     * @type {(function(*): void)}
     */
    const onStepChange = useCallback((newStep) => {
        if (step === PrintBrochureViewSteps.configureGroups && newStep === PrintBrochureViewSteps.productSelection) {
            dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
                open: true,
                title: 'Going Back',
                description: 'You will lose any changes made to the products of this poster. Are you sure you want to go back?',
                cancelButtonText: 'Stay',
                confirmButtonText: 'Go back',
                onClose: async (confirmed) => {
                    if (confirmed) {
                        setStep(step, newStep);
                    }
                    return true;
                }
            }));
        } else {
            setStep(step, newStep);
        }
    }, [dispatch, setStep, step])

    return (
        <Stepper
            steps={steps}
            activeStepId={step}
            onClicked={onStepChange}
            className={'w-75 my-5'}
        />
    );
};

export default PrintCatalogueViewStepperSection;
